import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxfAccountingV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixf-accounting-v1-account-query-service-account-get';
import { DEFAULT_PAGE_LAYOUT_GAP, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { IxfAccountingV1AccountState } from '@interxfi/types/generated/api';
import { PageTabRail } from '../../../../../../../../../../../../components/page-tab-rail/page-tab-rail';
import {
  type PortalTabbedNavigationTab,
  TabbedNavigationGroup,
} from '../../../../../../../../../../../../components/router/router-tabbed-navigation';
import { AccountingCoaAccountBreadcrumb } from '../../../../../../../../../../../../features/accounting/chart-of-accounts/accounting-coa-account-breadcrumb';
import { getAccountingAccountStateListItems } from '../../../../../../../../../../../../features/accounting/chart-of-accounts/chart-of-accounts-list-items';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/finance/_finance/accounting/account/_account/$accountId/_layout/settings',
)({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ClearingFinanceAccountingCoa,
      label: 'Settings',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ClearingFinanceAccountingCoa>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-settings-${params.accountId}`,
        label: <AccountingCoaAccountBreadcrumb accountId={params.accountId} />,
        to: `/clearing/finance/accounting/account/${params.accountId}/settings`,
        suffix: <CopyButton content={params.accountId} label="Copy Account ID" tooltip={{ copy: params.accountId, copied: 'Copied Account ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  component: ChartOfAccountsAccountSettings,
});

function getListItems(data: IxfAccountingV1AccountState | undefined) {
  const items = getAccountingAccountStateListItems(data);

  return {
    general: {
      left: [items.data.spec.number, items.data.spec.label, items.data.classification, items.data.spec.description, items.data.mapCodes],
      right: [items.metadata.createdAt, items.metadata.updatedAt, items.accountId],
    },
  };
}

function ChartOfAccountsAccountSettings() {
  const { accountId } = Route.useParams();
  const request = { accountId } as const;
  const { data, error, isPending } = useIxfAccountingV1AccountQueryServiceAccountGet(request);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.account), [data?.account]);

  return (
    <PageLayout.Section>
      <VerticalStack gap={7}>
        <PageTabRail group={TabbedNavigationGroup.ClearingFinanceAccountingCoa} />

        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </VerticalStack>
    </PageLayout.Section>
  );
}
